import { createRng, weightedPick } from './random.js';
import { DIFFICULT_COMBOS, WORDLISTS } from './wordlists.js';
export const MAX_TEXT_LENGTH = 20000;
export const MAX_CUSTOM_TEXT_LENGTH = 5000;
const EASY_POOL_SIZE = 150;
const DEFAULT_WORD_COUNT = 25;
/** Palabras necesarias para no agotar el texto en modo tiempo (hasta ~270 WPM). */
export function wordsForDuration(durationSeconds) {
    return Math.max(40, Math.ceil(durationSeconds * 4.5));
}
/** Normaliza texto libre: tipografía escribible, sin controles, espacios simples. */
export function sanitizeText(text, maxLength = MAX_CUSTOM_TEXT_LENGTH) {
    const normalized = text
        .normalize('NFC')
        .replace(/[“”«»„]/g, '"')
        .replace(/[‘’‚]/g, "'")
        .replace(/[–—]/g, '-')
        .replace(/…/g, '...')
        .replace(/\s+/g, ' ')
        .replace(/[\p{C}]/gu, '')
        .trim();
    if (normalized.length <= maxLength)
        return normalized;
    const cut = normalized.slice(0, maxLength);
    const lastSpace = cut.lastIndexOf(' ');
    return (lastSpace > 0 ? cut.slice(0, lastSpace) : cut).trim();
}
function pool(language, difficulty = 'normal') {
    const all = WORDLISTS[language];
    if (difficulty === 'easy')
        return all.slice(0, EASY_POOL_SIZE);
    if (difficulty === 'hard') {
        const long = all.filter((w) => w.length >= 6);
        return long.length >= 50 ? long : all;
    }
    return all;
}
/** Elige palabras evitando repetir la inmediatamente anterior. */
function pickWords(rng, words, count) {
    const result = [];
    let previous = '';
    for (let i = 0; i < count; i++) {
        let word = rng.pick(words);
        if (word === previous && words.length > 1)
            word = rng.pick(words);
        result.push(word);
        previous = word;
    }
    return result;
}
function capitalize(word) {
    return word.charAt(0).toUpperCase() + word.slice(1);
}
function numberToken(rng, language) {
    if (rng.chance(0.3)) {
        const separator = language === 'es' ? ',' : '.';
        return `${rng.int(0, 999)}${separator}${String(rng.int(0, 99)).padStart(2, '0')}`;
    }
    const digits = rng.int(1, 4);
    const min = digits === 1 ? 0 : 10 ** (digits - 1);
    return String(rng.int(min, 10 ** digits - 1));
}
function punctuatedSentences(rng, language, totalWords) {
    const words = pool(language);
    const tokens = [];
    while (tokens.length < totalWords) {
        const length = rng.int(3, 9);
        const sentence = pickWords(rng, words, length);
        sentence[0] = capitalize(sentence[0] ?? '');
        for (let i = 0; i < sentence.length - 1; i++) {
            const roll = rng.next();
            if (roll < 0.12)
                sentence[i] += ',';
            else if (roll < 0.15)
                sentence[i] += ';';
            else if (roll < 0.17)
                sentence[i] += ':';
            else if (roll < 0.21)
                sentence[i] = `"${sentence[i]}"`;
            else if (roll < 0.23)
                sentence[i] = `(${sentence[i]})`;
        }
        const ending = rng.next();
        const lastIndex = sentence.length - 1;
        if (ending < 0.15) {
            if (language === 'es')
                sentence[0] = `¿${sentence[0]}`;
            sentence[lastIndex] += '?';
        }
        else if (ending < 0.25) {
            if (language === 'es')
                sentence[0] = `¡${sentence[0]}`;
            sentence[lastIndex] += '!';
        }
        else {
            sentence[lastIndex] += '.';
        }
        tokens.push(...sentence);
    }
    return tokens.slice(0, Math.max(totalWords, tokens.length));
}
const SYMBOL_TEMPLATES = [
    (w) => `@${w}`,
    (w) => `#${w}`,
    (_, n) => `$${n}`,
    (_, n) => `${n}%`,
    (w) => `${w}_${w.length}`,
    (w) => `(${w})`,
    (w) => `[${w}]`,
    (w) => `{${w}}`,
    (w) => `<${w}>`,
    (w, n) => `${w}/${n}`,
    (w, n) => `${w}=${n}`,
    (_, n) => `${n}+${n.length}`,
    (_, n) => `${n}*2`,
    (w) => `${w}&${w.charAt(0)}`,
    (w) => `"${w}"`,
    (w) => `${w}-${w.length}`,
    (w) => `${w}|${w.charAt(0)}`,
    (w) => `${w}\\${w.length}`,
];
function symbolTokens(rng, language, count) {
    const words = pool(language, 'easy');
    const tokens = [];
    for (let i = 0; i < count; i++) {
        const word = rng.pick(words);
        if (rng.chance(0.35)) {
            tokens.push(word);
        }
        else {
            const template = rng.pick(SYMBOL_TEMPLATES);
            tokens.push(template(word, String(rng.int(1, 99))));
        }
    }
    return tokens;
}
function pseudoWord(rng, chars, min = 2, max = 5) {
    let word = '';
    const length = rng.int(min, max);
    for (let i = 0; i < length; i++)
        word += rng.pick(chars);
    return word;
}
function letterTokens(rng, language, letters, count) {
    const allowed = [...new Set([...letters.normalize('NFC')].filter((c) => c.trim() !== ''))];
    if (allowed.length === 0)
        return pickWords(rng, pool(language, 'easy'), count);
    const allowedSet = new Set(allowed);
    const real = WORDLISTS[language].filter((w) => [...w].every((c) => allowedSet.has(c)));
    const tokens = [];
    for (let i = 0; i < count; i++) {
        tokens.push(real.length >= 8 && rng.chance(0.6) ? rng.pick(real) : pseudoWord(rng, allowed));
    }
    return tokens;
}
function comboTokens(rng, language, count) {
    const combos = DIFFICULT_COMBOS[language];
    const words = WORDLISTS[language].filter((w) => combos.some((c) => w.includes(c)));
    const tokens = [];
    for (let i = 0; i < count; i++) {
        if (words.length >= 5 && rng.chance(0.7))
            tokens.push(rng.pick(words));
        else
            tokens.push(rng.pick(combos).repeat(rng.int(1, 2)));
    }
    return tokens;
}
function weakTokens(rng, language, weakKeys, count) {
    const keys = weakKeys.filter((k) => k.length > 0 && k !== ' ');
    if (keys.length === 0)
        return pickWords(rng, pool(language), count);
    const words = WORDLISTS[language];
    const weights = words.map((w) => keys.reduce((acc, key) => acc + (w.toLowerCase().includes(key.toLowerCase()) ? 1 : 0), 0));
    const hasCandidates = weights.some((w) => w > 0);
    const tokens = [];
    for (let i = 0; i < count; i++) {
        if (hasCandidates && rng.chance(0.75))
            tokens.push(weightedPick(rng, words, weights));
        else
            tokens.push(pseudoWord(rng, [...keys, ...'asdfjkl'.split('')], 3, 5));
    }
    return tokens;
}
function repeatToLength(text, minChars) {
    if (text.length === 0)
        return text;
    let result = text;
    while (result.length < minChars)
        result += ` ${text}`;
    return result;
}
function fromSources(rng, sources, minChars) {
    const clean = sources.map((s) => sanitizeText(s, MAX_TEXT_LENGTH)).filter(Boolean);
    if (clean.length === 0)
        return '';
    const order = [...clean];
    for (let i = order.length - 1; i > 0; i--) {
        const j = rng.int(0, i);
        [order[i], order[j]] = [order[j], order[i]];
    }
    let result = '';
    let index = 0;
    while (result.length < minChars || result.length === 0) {
        result = result ? `${result} ${order[index % order.length]}` : order[0];
        index++;
        if (index > 500)
            break;
    }
    return result;
}
/** Genera el texto objetivo. Nunca devuelve espacios iniciales/finales. */
export function generateText(config, seed) {
    const rng = createRng(seed);
    const timed = config.durationSeconds !== undefined && config.durationSeconds > 0;
    const count = timed ? wordsForDuration(config.durationSeconds) : Math.max(1, config.wordCount ?? DEFAULT_WORD_COUNT);
    const minChars = timed ? count * 6 : 0;
    let tokens;
    switch (config.mode) {
        case 'words':
            tokens = pickWords(rng, pool(config.language, config.difficulty), count);
            break;
        case 'numbers':
            tokens = Array.from({ length: count }, () => numberToken(rng, config.language));
            break;
        case 'punctuation':
            tokens = punctuatedSentences(rng, config.language, count);
            break;
        case 'symbols':
            tokens = symbolTokens(rng, config.language, count);
            break;
        case 'letters':
            tokens = letterTokens(rng, config.language, config.letters ?? '', count);
            break;
        case 'combos':
            tokens = comboTokens(rng, config.language, count);
            break;
        case 'weak':
            tokens = weakTokens(rng, config.language, config.weakKeys ?? [], count);
            break;
        case 'custom': {
            const text = sanitizeText(config.customText ?? '');
            if (config.customAsWordList) {
                const list = text.split(' ').filter(Boolean);
                tokens = list.length > 0 ? pickWords(rng, list, timed ? count : Math.max(list.length, config.wordCount ?? list.length)) : [];
                break;
            }
            return sanitizeText(timed ? repeatToLength(text, minChars) : text, MAX_TEXT_LENGTH);
        }
        case 'phrases':
        case 'texts':
            return sanitizeText(fromSources(rng, config.sourceTexts ?? [], minChars), MAX_TEXT_LENGTH);
    }
    return sanitizeText(tokens.join(' '), MAX_TEXT_LENGTH);
}
//# sourceMappingURL=generator.js.map